'use client';

import { Button, type ButtonProps } from '@base-ui/react';
import clsx from 'clsx';
import type { HTMLAttributeAnchorTarget, KeyboardEvent, MouseEvent, RefObject } from 'react';

import { SDSLink } from '@repo/sds/components/SDSLink';

import './SDSPrimitiveButton.scss';

export interface SDSPrimitiveButtonProps extends Omit<ButtonProps, 'className' | 'onClick'> {
  className?: string;
  isDisabled?: boolean;
  dataTestId?: string;
  href?: string;
  type?: 'button' | 'submit' | 'reset';
  target?: HTMLAttributeAnchorTarget;
  buttonRef?: RefObject<HTMLButtonElement | null> | null;
  onClick?: (event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => void;
  onLinkClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  // Accessibility props
  ariaLabel?: string;
  ariaDescribedBy?: string;
  ariaPressed?: boolean;
  ariaBusy?: boolean;
  ariaExpanded?: boolean;
  ariaControls?: string;
  ariaCurrent?: 'page' | 'step' | 'location' | 'date' | 'time' | 'true' | 'false';
}

export function SDSPrimitiveButton({
  children,
  className,
  onClick,
  onLinkClick,
  isDisabled,
  dataTestId,
  type = 'button',
  href,
  buttonRef,
  target,
  ariaLabel,
  ariaDescribedBy,
  ariaPressed,
  ariaBusy,
  ariaExpanded,
  ariaControls,
  ariaCurrent,
  ...baseProps
}: SDSPrimitiveButtonProps) {
  const buttonClassName = clsx('SDSPrimitiveButton', className);

  const handleAction = (event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLButtonElement>) => {
    if (isDisabled) return;
    onClick?.(event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    // A submit/reset button must keep its native keyboard activation: cancelling it here would leave the form
    // unsubmittable from the keyboard. The browser's own click still reaches `handleAction`.
    if (type !== 'button') return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleAction(event);
    }
  };

  const handleLinkClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (isDisabled) {
      event.preventDefault();
      return;
    }
    onLinkClick?.(event);
  };

  const ariaProps = {
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    'aria-pressed': ariaPressed,
    'aria-busy': ariaBusy,
    'aria-expanded': ariaExpanded,
    'aria-controls': ariaControls,
    // Reached the link branch already; a real button was dropping it, so a
    // control marking the page it is on told a screen reader nothing.
    'aria-current': ariaCurrent,
  };

  if (href) {
    return (
      <SDSLink
        className={buttonClassName}
        href={href}
        target={target}
        dataTestId={dataTestId}
        ariaLabel={ariaLabel}
        ariaDescribedBy={ariaDescribedBy}
        ariaCurrent={ariaCurrent}
        onClick={handleLinkClick}
      >
        {children}
      </SDSLink>
    );
  }

  return (
    <Button
      ref={buttonRef}
      className={buttonClassName}
      onClick={handleAction}
      onKeyDown={handleKeyDown}
      disabled={isDisabled}
      data-testid={dataTestId}
      type={type}
      {...ariaProps}
      {...baseProps}
    >
      {children}
    </Button>
  );
}
